import { B2BProductOfferingProto } from "@noom/noom-contracts/noom_contracts/backend/enrollment/b2b/enrollment_offerings";

import { Curriculum, ProgramOffering } from "@/constants";
import { EnrollmentInformation, Partner } from "@/models";

import { buildOfferingFromPrograms } from "./programs/buildOfferingFromPrograms";
import { productOfferingToProgramOfferings } from "./programs/productOfferingToProgramOfferings";

const CURRICULUM_TO_PROGRAM_OFFERING_MAP: Record<
  Curriculum,
  ProgramOffering | undefined
> = {
  [Curriculum.CURRICULUM_UNSPECIFIED]: undefined,
  [Curriculum.DPP]: "DPP",
  [Curriculum.HM]: "MOOD",
  [Curriculum.HW]: "WEIGHT",
};

export const PROGRAM_OFFERING_TO_CURRICULUM_MAP: Record<
  ProgramOffering,
  Curriculum
> = {
  DPP: Curriculum.DPP,
  MED: Curriculum.HW,
  MOOD: Curriculum.HM,
  NMPBA: Curriculum.HW,
  WEIGHT: Curriculum.HW,
  DIABETES_LIFESTYLE: Curriculum.HW,
};

/**
 * Gets the curriculum associated with a program.
 *
 * @param {ProgramOffering | null} program
 * @returns {Curriculum}
 */
export const getCurriculumFromProgramOffering = (
  program: ProgramOffering | null,
): Curriculum => {
  if (program === null) {
    return Curriculum.CURRICULUM_UNSPECIFIED;
  }

  return PROGRAM_OFFERING_TO_CURRICULUM_MAP[program];
};

/**
 * Get partner offerings all of the given programs - used to know if
 * certain pages or features should be gated behind those programs.
 *
 * @param enrollmentInfo Enrollment info of the UPID - used to check what
 * programs the UPID can be enrolled into
 * @param partnerInfo Information about the partner - used to check if certain
 * configs are enabled (e.g. if Noom Med is offered)
 * @returns List of programs that the partner offers
 */
export const getPartnerProgramOfferings = (
  enrollmentInfo: EnrollmentInformation | undefined,
  partnerInfo: Partner | null,
): ProgramOffering[] => {
  const { eligiblePrograms = [], triagePrograms = [] } = enrollmentInfo || {};
  const { configs: { OFFERS_MED = false } = {} } = partnerInfo || {};

  const setOfProgramOfferings = new Set<Curriculum>([
    ...eligiblePrograms,
    ...triagePrograms,
  ]);

  const partnerProgramOfferings = Array.from(setOfProgramOfferings)
    .map((curriculum) => CURRICULUM_TO_PROGRAM_OFFERING_MAP[curriculum])
    .filter((program) => program) as ProgramOffering[];

  if (OFFERS_MED) {
    partnerProgramOfferings.push("MED");
  }

  return partnerProgramOfferings;
};

/**
 * Reads a B2B product offering composed of program and coursepacks to
 * determine the matching frontend program offering
 *
 * @param productOffering the product offering representation
 * @returns
 */
export const getProgramFromOffering = (
  productOffering: B2BProductOfferingProto | undefined,
): ProgramOffering | null => {
  if (!productOffering) {
    return null;
  }

  const { program, coursePacks } = productOffering;

  switch (program) {
    case "DIABETES_PREVENTION":
      return "DPP";
    case "HEALTHY_MIND":
      return "MOOD";
    case "HEALTHY_WEIGHT":
      if (coursePacks.find(({ value }) => value === "MED")) {
        return "MED";
      }
      if (coursePacks.find(({ value }) => value === "DIABETES_SUPPORT")) {
        return "DIABETES_LIFESTYLE";
      }

      return "WEIGHT";
    default:
      return null;
  }
};

export const getOfferingFromProgram = (
  program: ProgramOffering,
  qualifiedPrograms: ProgramOffering[],
): B2BProductOfferingProto => {
  // currently we are building hardcoded offerings and ignoring coursepacks sent from the backend.
  // Supporting coursepacks sent from the backend is another level of complexity which we don't need yet.
  switch (program) {
    // For MED / NMPBA, we need to include DIABETES_LIFESTYLE if the user is qualified for it
    case "MED": {
      const programs: ProgramOffering[] = ["WEIGHT", "MED"];
      if (qualifiedPrograms.includes("DIABETES_LIFESTYLE")) {
        programs.push("DIABETES_LIFESTYLE");
      }
      return buildOfferingFromPrograms(programs);
    }
    case "NMPBA": {
      const programs: ProgramOffering[] = ["WEIGHT", "NMPBA"];
      if (qualifiedPrograms.includes("DIABETES_LIFESTYLE")) {
        programs.push("DIABETES_LIFESTYLE");
      }
      return buildOfferingFromPrograms(programs);
    }
    case "WEIGHT":
      return buildOfferingFromPrograms(["WEIGHT"]);
    case "DPP":
      return buildOfferingFromPrograms(["DPP"]);
    case "DIABETES_LIFESTYLE":
      return buildOfferingFromPrograms(["WEIGHT", "DIABETES_LIFESTYLE"]);
    default:
      throw new Error(`Cannot convert unsupported program: ${program}`);
  }
};

/**
 * Determines the list of programs a user is eligible for based on the product
 * offerings the user is eligible for
 *
 * @param eligibleOfferings List of product offerings that a user is eligible for
 * @returns List of programs that the product offerings represent
 */
export const getEligibleProgramsFromEligibleOfferings = (
  eligibleOfferings: B2BProductOfferingProto[],
  enrollmentInfo?: EnrollmentInformation,
): ProgramOffering[] => {
  const eligiblePrograms = eligibleOfferings.flatMap((offering) =>
    Array.from(productOfferingToProgramOfferings(offering)),
  );

  // The property on enrollmentInfo is called "eligiblePrograms" but it has
  // always contained curriculums - renaming it here for clarity, but this will
  // be removed once the backend properly returns MOOD program.
  const { eligiblePrograms: upidCurriculums = [] } = enrollmentInfo || {};
  if (upidCurriculums.includes(Curriculum.HM)) {
    eligiblePrograms.push("MOOD");
  }

  return Array.from(new Set(eligiblePrograms));
};

/**
 * Determines if a partner offers only DPP and no other programs
 *
 * @param enrollmentInfo
 * @param partnerInfo
 * @returns
 */
export const partnerOffersDppOnly = (
  eligiblePrograms: ProgramOffering[],
): boolean =>
  eligiblePrograms.includes("DPP") && !eligiblePrograms.includes("WEIGHT");

/**
 * Determines if a partner offers only DPP and no other programs without
 * offering Diabetes Management. This differs from partners with Diabetes
 * Management because of different qualification criteria between DPP and
 * Weight + DM.
 *
 * @param enrollmentInfo
 * @param partnerInfo
 * @returns
 */
export const partnerOffersDppOnlyWithoutDM = (
  eligiblePrograms: ProgramOffering[],
): boolean => eligiblePrograms.length === 1 && eligiblePrograms.includes("DPP");

/**
 * Determines if a partner offers only DPP and no other programs but also
 * offers Weight + Diabetes Management. This differs from partners without
 * Diabetes Management because of different qualification criteria between DPP
 * and Weight + DM.
 *
 * @param enrollmentInfo
 * @param partnerInfo
 * @returns
 */
export const partnerOffersDppOnlyWithDM = (
  eligiblePrograms: ProgramOffering[],
): boolean =>
  eligiblePrograms.length === 2 &&
  eligiblePrograms.includes("DPP") &&
  eligiblePrograms.includes("DIABETES_LIFESTYLE");
